import { useState } from 'react';
import axios from 'axios';
import { Copy, ExternalLink, Link2, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { StatusDot } from '@/components/Page';
import { API_URL } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { comRespostaBelluno, descricaoPagamentoBelluno, linkBelluno, linkBellunoAtivo, textoVencimento, tomDoLink } from '@/lib/belluno';

const copiar = async (url) => {
  try {
    await navigator.clipboard.writeText(url);
    toast.success('Link copiado! Envie ao cliente.');
  } catch {
    toast.error('Não foi possível copiar o link');
  }
};

const erroDaBelluno = (e, padrao = 'Erro ao falar com a Belluno') => {
  const detail = e.response?.data?.detail;
  toast.error(typeof detail === 'string' ? detail : padrao);
};

// Gera e copia o link, como o checkout da Pagar.me: o do pedido inteiro (desfaz um split sem pagamento) ou, com
// `parte`, um novo para a parte Belluno cujo link venceu. O teto de parcelas é o escolhido no pedido ou na parte; o
// link cobra só no cartão (BELLUNO_METODOS=1 na API desde 2026-10-09). Devolve a resposta, ou null no erro.
export async function gerarLinkBelluno(pedido, parte = null) {
  try {
    const { data } = await axios.post(`${API_URL}/pedidos/${pedido.id}/belluno/link`, parte == null ? {} : { parte });
    if (data.belluno_link?.link) await copiar(data.belluno_link.link);
    return data;
  } catch (e) {
    erroDaBelluno(e, 'Erro ao gerar o link Belluno');
    return null;
  }
}

// Chama uma rota da Belluno do pedido, com o botão ocupado enquanto isso e o erro em toast. `onErro`: a tela relê o
// pedido (a Belluno pode ter falhado no meio de uma troca, e o link mostrado ali pode já estar morto).
const useRotaBelluno = (pedido, onChanged, onErro) => {
  const [busy, setBusy] = useState(null);
  const chamar = async (acao, corpo, ok) => {
    setBusy(acao);
    try {
      const { data } = await axios.post(`${API_URL}/pedidos/${pedido.id}/belluno/${acao}`, corpo);
      onChanged?.(data);
      await ok?.(data);
    } catch (e) {
      erroDaBelluno(e);
      onErro?.();
    } finally {
      setBusy(null);
    }
  };
  return [busy, chamar];
};

// `pago`: o cliente pagou no instante do cancelamento (a Belluno recusa cancelar link pago) e a API registrou o pagamento.
const avisoDoCancelamento = (d) => {
  if (d.pago) toast.warning('O cliente pagou este link antes do cancelamento: o pagamento foi registrado. Não cobre de novo.');
  else if (d.confirmado === false) toast.warning('Link cancelado no ERP. A Belluno ainda não confirmou: o ERP vai tentar de novo e avisa se ele for pago.');
  else toast.success('Link Belluno cancelado');
};

// O que a reconsulta achou no link (do pedido ou da parte), em toast.
const avisoDaConsulta = (pedido, parte) => (d) => {
  const link = linkBelluno(comRespostaBelluno(pedido, d), parte);
  toast.success(link?.pago ? 'Pagamento confirmado pela Belluno' : `Link ${String(link?.status_label || '').toLowerCase()}`);
};

// O estado do link do pedido inteiro no card do pedido: aberto (copiar, atualizar, cancelar), pago (como pagou) ou
// encerrado.
export function EstadoLinkBelluno({ pedido, onChanged, onErro, podeAgir = true }) {
  const link = linkBelluno(pedido);
  const [busy, chamar] = useRotaBelluno(pedido, onChanged, onErro);
  if (!link?.transaction_id) return null;

  const ativo = linkBellunoAtivo(pedido);
  const vencimento = ativo ? textoVencimento(link) : '';
  const frase = link.pago
    ? `Pago pela Belluno por ${descricaoPagamentoBelluno(link)}`
    : `Link Belluno ${String(link.status_label || '').toLowerCase()}${vencimento ? `, ${vencimento}` : ''}`;

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm" data-testid={`link-belluno-${pedido.numero_pedido}`}>
      <StatusDot tone={tomDoLink(link)} className="text-[13px]">
        <span>{frase}</span>
        {link.motivo && !link.pago && <span className="text-gray-500"> · {link.motivo}</span>}
      </StatusDot>
      {ativo && (
        <div className="flex flex-wrap items-center gap-1">
          <Button size="sm" variant="outline" onClick={() => copiar(link.link)}>
            <Copy className="h-4 w-4" />
            Copiar link
          </Button>
          <Button asChild size="sm" variant="ghost" className="text-gray-500">
            <a href={link.link} target="_blank" rel="noopener noreferrer" aria-label="Abrir o link" title="Abrir o link">
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
          <Button
            size="sm" variant="ghost" className="text-gray-500" disabled={busy === 'sincronizar'} title="Consultar a Belluno agora"
            onClick={() => chamar('sincronizar', null, avisoDaConsulta(pedido, null))}
          >
            {busy === 'sincronizar' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Atualizar
          </Button>
          {podeAgir && (
            <Button
              size="sm" variant="ghost" className="text-gray-500 hover:text-red-700 dark:hover:text-red-300" disabled={busy === 'cancelar-link'}
              onClick={() => {
                if (window.confirm('Cancelar o link Belluno? O cliente não conseguirá mais pagar por ele.')) {
                  chamar('cancelar-link', null, avisoDoCancelamento);
                }
              }}
            >
              {busy === 'cancelar-link' ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
              Cancelar link
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

const ICONE = 'inline-flex h-6 w-6 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-blue-500/10 hover:text-blue-500 disabled:opacity-50 dark:hover:text-blue-400';

// Uma parte Belluno do pagamento dividido, na linha dela: o estado do link e, aberto, copiar, consultar e cancelar;
// vencido, recusado ou cancelado, um link novo só desta parte (o split fica como está).
export function AcoesParteBelluno({ pedido, parte, onChanged, onErro, podeAgir = true }) {
  const [busy, chamar] = useRotaBelluno(pedido, onChanged, onErro);
  const link = linkBelluno(pedido, parte);
  const paga = (pedido.pagamento_split || []).some((p) => p.indice === parte && p.pago);
  if (paga) return <StatusDot tone="ok" className="text-[12.5px]">Pago</StatusDot>;

  if (!linkBellunoAtivo(pedido, parte)) {
    return (
      <>
        <StatusDot tone={link ? tomDoLink(link) : 'att'} className="text-[12.5px]">
          {link ? `Link ${String(link.status_label || 'encerrado').toLowerCase()}` : 'Sem link'}
        </StatusDot>
        {podeAgir && (
          <button
            type="button"
            disabled={busy === 'link'}
            onClick={() => chamar('link', { parte }, (d) => d.belluno_link?.link && copiar(d.belluno_link.link))}
            title={`Gerar e copiar um link novo para o pagamento ${parte}`}
            className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 transition-colors hover:bg-blue-100 disabled:opacity-50 dark:bg-blue-500/10 dark:text-blue-300 dark:hover:bg-blue-500/20"
            data-testid={`novo-link-belluno-${parte}`}
          >
            {busy === 'link' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />}
            Novo link
          </button>
        )}
      </>
    );
  }

  // Aberto mas não "open": processando ou em análise na Belluno.
  const vencimento = textoVencimento(link);
  const frase = String(link.status || '').toLowerCase() === 'open'
    ? `Aguardando${vencimento ? `, ${vencimento}` : ''}`
    : link.status_label;
  return (
    <>
      <StatusDot tone="run" className="text-[12.5px]">{frase}</StatusDot>
      <button type="button" onClick={() => copiar(link.link)} title={`Copiar o link do pagamento ${parte}`} className={ICONE}>
        <Copy className="h-3.5 w-3.5" />
      </button>
      <button
        type="button" disabled={busy === 'sincronizar'} title="Consultar a Belluno agora" className={ICONE}
        onClick={() => chamar('sincronizar', null, avisoDaConsulta(pedido, parte))}
      >
        {busy === 'sincronizar' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
      </button>
      {podeAgir && (
        <button
          type="button" disabled={busy === 'cancelar-link'} title={`Cancelar o link do pagamento ${parte}`}
          className={cn(ICONE, 'hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400')}
          onClick={() => {
            if (window.confirm(`Cancelar o link Belluno do pagamento ${parte}? O cliente não conseguirá mais pagar por ele.`)) {
              chamar('cancelar-link', { parte }, avisoDoCancelamento);
            }
          }}
          data-testid={`cancelar-link-belluno-${parte}`}
        >
          {busy === 'cancelar-link' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />}
        </button>
      )}
    </>
  );
}
